import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Modal, Form, Input, Switch, Space, Tag, Popconfirm,
  message, Typography, Table, Avatar, Alert, Tabs, List, Empty, Tooltip, Badge,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined, SendOutlined,
  MessageOutlined, TeamOutlined, UserOutlined, RobotOutlined,
  CheckCircleOutlined, StopOutlined, SettingOutlined, CopyOutlined,
} from '@ant-design/icons';
import { lineAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function LineSettingsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [messages_, setMessages_] = useState([]);
  const [messagesAccount, setMessagesAccount] = useState(null);
  const [messagesFilter, setMessagesFilter] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendAccountId, setSendAccountId] = useState(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [sendForm] = Form.useForm();

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lineAPI.listAccounts();
      setAccounts(res.data || res);
    } catch (e) {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      await lineAPI.addAccount(values);
      message.success('LINE Bot 已新增');
      setAddModalOpen(false);
      addForm.resetFields();
      fetchAccounts();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    editForm.setFieldsValue({
      system_prompt: account.system_prompt || '',
      product_context: account.product_context || '',
      is_active: account.is_active,
      group_reply_enabled: account.group_reply_enabled,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await lineAPI.updateAccount(editingAccount.id, values);
      message.success('已更新');
      setEditModalOpen(false);
      fetchAccounts();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await lineAPI.deleteAccount(id);
      message.success('已刪除');
      fetchAccounts();
    } catch (e) {
      message.error('刪除失敗');
    }
  };

  const handleRefresh = async (id) => {
    try {
      await lineAPI.refreshAccount(id);
      message.success('已重新整理');
      fetchAccounts();
    } catch (e) {
      message.error('重新整理失敗');
    }
  };

  const handleToggleGroupReply = async (account) => {
    const newVal = !account.group_reply_enabled;
    try {
      await lineAPI.toggleGroupReply(account.id, newVal);
      message.success(newVal ? '群組自動回覆已開啟' : '群組自動回覆已關閉');
      fetchAccounts();
    } catch (e) {
      message.error('切換失敗');
    }
  };

  const openMessages = async (account, sourceType) => {
    setMessagesAccount(account);
    setMessagesFilter(sourceType || null);
    try {
      const res = await lineAPI.getMessages(account.id, sourceType);
      setMessages_(res.data || res);
    } catch {
      setMessages_([]);
    }
  };

  const handleSend = async () => {
    try {
      const values = await sendForm.validateFields();
      await lineAPI.sendMessage(sendAccountId, values);
      message.success('已發送');
      setSendModalOpen(false);
      sendForm.resetFields();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const copyWebhookUrl = () => {
    const url = `${window.location.protocol}//${window.location.host}/api/v1/line/webhook`;
    navigator.clipboard.writeText(url);
    message.success('Webhook URL 已複製');
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>LINE Bot 管理</Title>}
        extra={
          <Space>
            <Tooltip title="將此 URL 設定到 LINE Developers Console → Messaging API → Webhook URL">
              <Button icon={<CopyOutlined />} onClick={copyWebhookUrl}>
                複製 Webhook URL
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { addForm.resetFields(); setAddModalOpen(true); }}>
              新增 LINE Bot
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="設定步驟"
          description={
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>在 LINE Developers Console 建立 Messaging API Channel</li>
              <li>取得 Channel Access Token（長期）和 Channel Secret</li>
              <li>在此頁面新增 Bot，貼上 Token 和 Secret</li>
              <li>複製上方 Webhook URL，貼到 LINE Console → Messaging API → Webhook URL</li>
              <li>在 LINE Console 開啟「Use webhook」並關閉「Auto-reply messages」</li>
              <li>如需群組功能：LINE Console → Messaging API → 開啟「Allow bot to join groups and multi-person chats」</li>
            </ol>
          }
        />

        {accounts.length === 0 && !loading ? (
          <Empty description="尚未新增 LINE Bot" />
        ) : (
          accounts.map(account => (
            <Card
              key={account.id}
              style={{ marginBottom: 16, borderRadius: 12, border: account.is_active ? '1px solid #91caff' : '1px solid #d9d9d9' }}
              styles={{ body: { padding: 20 } }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <Avatar
                  size={56}
                  src={account.picture_url}
                  style={{ background: '#06C755', flexShrink: 0 }}
                >
                  {(account.display_name || 'L')[0]}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 18 }}>{account.display_name || 'LINE Bot'}</Text>
                    {account.is_active
                      ? <Tag color="green">啟用中</Tag>
                      : <Tag>停用</Tag>
                    }
                    {account.basic_id && (
                      <Text type="secondary" style={{ fontSize: 12 }}>@{account.basic_id}</Text>
                    )}
                  </div>

                  {/* Group reply status */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', borderRadius: 8, marginBottom: 12,
                    background: account.group_reply_enabled ? '#f6ffed' : '#fff7e6',
                    border: account.group_reply_enabled ? '1px solid #b7eb8f' : '1px solid #ffd591',
                  }}>
                    <Badge status={account.group_reply_enabled ? 'success' : 'warning'} />
                    <Text style={{ fontSize: 13 }}>
                      群組自動回覆：
                      <Text strong style={{ color: account.group_reply_enabled ? '#52c41a' : '#fa8c16' }}>
                        {account.group_reply_enabled ? 'Bot 自動回覆中' : '已暫停 — 人工回覆模式'}
                      </Text>
                    </Text>
                    <Switch
                      size="small"
                      checked={account.group_reply_enabled}
                      onChange={() => handleToggleGroupReply(account)}
                      checkedChildren={<RobotOutlined />}
                      unCheckedChildren={<UserOutlined />}
                    />
                  </div>

                  {/* Actions */}
                  <div>
                    <Space wrap>
                      <Button size="small" icon={<SettingOutlined />} onClick={() => handleEdit(account)}>
                        教學設定
                      </Button>
                      <Button size="small" icon={<MessageOutlined />} onClick={() => openMessages(account)}>
                        對話紀錄
                      </Button>
                      <Button size="small" icon={<TeamOutlined />} onClick={() => openMessages(account, 'group')}>
                        群組訊息
                      </Button>
                      <Button size="small" icon={<SendOutlined />} onClick={() => { setSendAccountId(account.id); sendForm.resetFields(); setSendModalOpen(true); }}>
                        發送訊息
                      </Button>
                      <Button size="small" icon={<ReloadOutlined />} onClick={() => handleRefresh(account.id)}>
                        重新整理
                      </Button>
                      <Popconfirm title="確認刪除此 LINE Bot？" onConfirm={() => handleDelete(account.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </Card>

      {/* Add Account Modal */}
      <Modal
        title="新增 LINE Bot"
        open={addModalOpen}
        onOk={handleAdd}
        onCancel={() => setAddModalOpen(false)}
        width={600}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="channel_access_token" label="Channel Access Token（長期）" rules={[{ required: true, message: '必填' }]}>
            <TextArea rows={3} placeholder="從 LINE Developers Console → Messaging API → Channel access token" />
          </Form.Item>
          <Form.Item name="channel_secret" label="Channel Secret" extra="用於驗證 Webhook 簽名，建議填寫">
            <Input placeholder="從 LINE Developers Console → Basic settings → Channel secret" />
          </Form.Item>
          <Form.Item name="channel_id" label="Channel ID（選填）">
            <Input placeholder="LINE Channel ID" />
          </Form.Item>
          <Form.Item name="system_prompt" label="AI 角色設定">
            <TextArea rows={4} placeholder="例如：你是 Zoya，一位專業的 AI 教育訓練顧問..." />
          </Form.Item>
          <Form.Item name="product_context" label="教學內容（知識庫）">
            <TextArea rows={6} placeholder="輸入產品資訊、FAQ、教學材料..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Account Modal */}
      <Modal
        title={`設定：${editingAccount?.display_name || 'LINE Bot'}`}
        open={editModalOpen}
        onOk={handleEditSave}
        onCancel={() => setEditModalOpen(false)}
        width={700}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="system_prompt" label="AI 角色設定（系統提示詞）" tooltip="定義 Bot 的角色、語氣和行為">
            <TextArea rows={6} placeholder="例如：&#10;你是 Zoya，一位專業的 AI 教育訓練顧問。&#10;回答請用繁體中文，語氣友善專業。&#10;如果不確定答案請建議用戶聯繫客服。" />
          </Form.Item>
          <Form.Item name="product_context" label="教學內容（知識庫）" tooltip="Bot 回答問題的參考資料" extra="內容越詳細，Bot 回答越準確">
            <TextArea rows={10} placeholder="在此輸入所有產品資訊和 FAQ..." />
          </Form.Item>
          <Form.Item name="is_active" label="啟用 Bot" valuePropName="checked">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="group_reply_enabled" label="群組自動回覆" valuePropName="checked" extra="關閉後 Bot 不會在群組中自動回覆，但仍會記錄訊息">
            <Switch
              checkedChildren={<><RobotOutlined /> Bot 自動回覆</>}
              unCheckedChildren={<><UserOutlined /> 人工回覆</>}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Message Logs Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            {`對話紀錄：${messagesAccount?.display_name || ''}`}
            {messagesFilter === 'group' && <Tag color="blue">群組</Tag>}
          </Space>
        }
        open={!!messagesAccount}
        onCancel={() => { setMessagesAccount(null); setMessages_([]); }}
        footer={null}
        width={700}
      >
        <Tabs
          defaultActiveKey={messagesFilter || 'all'}
          onChange={(key) => {
            const filter = key === 'all' ? null : key;
            setMessagesFilter(filter);
            if (messagesAccount) openMessages(messagesAccount, filter);
          }}
          items={[
            { key: 'all', label: '全部' },
            { key: 'user', label: '私訊' },
            { key: 'group', label: '群組' },
          ]}
        />
        {messages_.length === 0 ? (
          <Empty description="尚無訊息紀錄" />
        ) : (
          <List
            dataSource={messages_}
            style={{ maxHeight: 500, overflow: 'auto' }}
            renderItem={(log) => (
              <List.Item style={{ padding: '8px 0', border: 'none', display: 'block' }}>
                <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag color={log.source_type === 'group' ? 'blue' : 'default'} style={{ fontSize: 11 }}>
                    {log.source_type === 'group' ? '群組' : '私訊'}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString('zh-TW') : ''}
                  </Text>
                  {log.is_auto_reply && <Tag color="green" style={{ fontSize: 10 }}>Bot 自動回覆</Tag>}
                </div>
                <div style={{
                  padding: '8px 12px', borderRadius: 8,
                  background: '#f5f5f5', marginBottom: 4,
                }}>
                  <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                  <Text>{log.content}</Text>
                </div>
                {log.reply_content && (
                  <div style={{
                    padding: '8px 12px', borderRadius: 8,
                    background: '#f0f9eb', marginLeft: 24,
                  }}>
                    <RobotOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                    <Text>{log.reply_content}</Text>
                  </div>
                )}
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Send Message Modal */}
      <Modal
        title="發送 LINE 訊息"
        open={sendModalOpen}
        onOk={handleSend}
        onCancel={() => setSendModalOpen(false)}
      >
        <Form form={sendForm} layout="vertical">
          <Form.Item name="to" label="收件人 ID（User ID 或 Group ID）" extra="留空並勾選廣播可發送給所有好友">
            <Input placeholder="U... 或 C..." />
          </Form.Item>
          <Form.Item name="message" label="訊息內容" rules={[{ required: true }]}>
            <TextArea rows={4} placeholder="輸入要發送的訊息..." />
          </Form.Item>
          <Form.Item name="broadcast" label="廣播給所有好友" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
